'use client'

import { algoliaAnalytics, AlgoliaEventName } from 'algolia/algolia'
import { algoliaTemplatesIndex } from 'algolia/algolia-templates'
import { useWindowDomEvent } from 'hooks/use-dom-event'
import type { Hit } from 'instantsearch.js/es/types'
import * as React from 'react'
import { nextFrame } from 'utils/dom'

import { getUrlFromTitleId } from '../search-results/utils'
import type { TextsType } from '../template-details'
import {
  canonicalizeStudioLocalePathname,
  stripStudioLocalePrefix,
} from '@repo/react-router-locale/canonicalize-studio-locale-pathname'
import { defaultLanguage } from 'cf-i18n/constants'
import { studioUrlSegmentToLanguage } from 'cf-i18n/helpers'

export interface AlgoliaTemplateHit {
  algoliaSyncTime: Date
  category: string
  createTime: Date
  feature: string
  fileUrl: string
  height: number
  imageUrl: string
  objectID: string
  price: string
  styles: string[]
  subcategory: string
  subject: string
  tags: string[]
  themes: string[]
  title: string
  topic: string
  type: string
  updateTime: Date
  userId: string
  width: number
  __queryID?: string
}

type TemplatesPageContextModel = {
  items: Hit<AlgoliaTemplateHit>[]
  activeItem: number
  navigateToTemplate: (hit: Hit<AlgoliaTemplateHit>, newHits?: Hit<AlgoliaTemplateHit>[]) => void
  lastLocation?: string
  setLastLocation: React.Dispatch<React.SetStateAction<string | undefined>>
  setActiveItem: React.Dispatch<React.SetStateAction<number>>
} & TemplatesPageProviderProps &
  TextsType

interface TemplatesPageProviderProps {
  AlgoliaProvider: React.ComponentType<React.PropsWithChildren>
  templatesGalleryRoute: string
  templatesItemRoute: string
  studioCreateUrl: string
  Link: React.ComponentType<React.ComponentPropsWithoutRef<'a'>>
  itemPageSlug?: string
  isStudio?: boolean
  isClubhouse?: boolean
  toCanonicalPathname?: (pathname: string) => string
}
const TemplatesPageContext = React.createContext<TemplatesPageContextModel>(null!)

export function TemplatesPageProvider({
  children,
  ...props
}: React.PropsWithChildren<TemplatesPageProviderProps> & TextsType) {
  const { templatesItemRoute, itemPageSlug } = props
  const toCanonicalPathname = props.toCanonicalPathname ?? stripStudioLocalePrefix

  // Specifies the item index to display in the modal, -1 means no modal is shown
  const [activeItem, setActiveItem] = React.useState<number>(-1)
  const [items, setItems] = React.useState<Hit<AlgoliaTemplateHit>[]>([])
  const [lastLocation, setLastLocation] = React.useState<string>()

  const ITEM_SLUG_REGEX = React.useMemo(
    () => new RegExp(`${templatesItemRoute}(.*?)/?$`),
    [templatesItemRoute],
  )

  const handleLocationChange = React.useCallback(
    (newHits?: Hit<AlgoliaTemplateHit>[]) => {
      React.startTransition(() => {
        const canonicalPathname = toCanonicalPathname(window.location.pathname)

        if (canonicalPathname.startsWith(templatesItemRoute)) {
          const urlSlug = canonicalPathname.match(ITEM_SLUG_REGEX)?.[1] || ''

          // itemPageSlug is passed only for the item page
          // if the current location matches it then we don't need a modal
          if (itemPageSlug === urlSlug) {
            setActiveItem(-1)
            return
          }

          const hits = newHits || items

          const hitIndex = hits.findIndex(
            hit => getUrlFromTitleId(hit.title, hit.objectID) === urlSlug,
          )

          if (hitIndex === activeItem) {
            return
          }

          setActiveItem(hitIndex)

          if (hitIndex >= 0) {
            algoliaAnalytics('clickedObjectIDsAfterSearch', {
              index: algoliaTemplatesIndex,
              eventName: AlgoliaEventName.PRODUCT_CLICKED,
              queryID: hits[hitIndex].__queryID || '',
              objectIDs: [hits[hitIndex].objectID],
              positions: [hits[hitIndex].__position || 0],
            })

            nextFrame(() => {
              document
                .querySelector(`#template-modal-scroll-target`)
                ?.scrollIntoView({ behavior: 'smooth' })
            })
          } else {
            // TODO: If the user navigates back in history, it can cause an edge-case where the items list is no longer in sync
            // eslint-disable-next-line no-console
            console.error('Hit not found')
          }
        } else {
          setActiveItem(-1)
        }
      })
    },
    [ITEM_SLUG_REGEX, activeItem, itemPageSlug, items, templatesItemRoute, toCanonicalPathname],
  )

  useWindowDomEvent('popstate', () => handleLocationChange())
  useWindowDomEvent('cf-popstate', () => handleLocationChange())

  const navigateToTemplate = React.useCallback(
    (hit: Hit<AlgoliaTemplateHit>, newHits?: Hit<AlgoliaTemplateHit>[]) => {
      if (newHits) {
        setItems(newHits)
      }

      // We need to save the current location (only if we don't currently have a modal open)
      // so that we can restore it when the modal is closed.
      if (activeItem === -1) {
        setLastLocation(`${window.location.pathname}${window.location.search}`)
      }

      const firstSegment = window.location.pathname.split('/').filter(Boolean)[0]
      const language = (firstSegment && studioUrlSegmentToLanguage(firstSegment)) || defaultLanguage
      const itemPath = `${templatesItemRoute}${getUrlFromTitleId(hit.title, hit.objectID)}`
      const localizedItemPath = `${canonicalizeStudioLocalePathname(itemPath, language)}/`

      window.history.pushState({}, '', localizedItemPath)

      handleLocationChange(newHits)
    },
    [activeItem, handleLocationChange, templatesItemRoute],
  )

  return (
    <TemplatesPageContext.Provider
      value={{
        items,
        activeItem,
        navigateToTemplate,
        lastLocation,
        setLastLocation,
        setActiveItem,
        ...props,
        toCanonicalPathname,
      }}
    >
      {children}
    </TemplatesPageContext.Provider>
  )
}

export const useTemplatesPageContext = () => {
  const context = React.useContext(TemplatesPageContext)

  if (context === undefined) {
    throw new Error('useTemplatesContext must be used within a TemplatesPageProvider')
  }

  return context
}
